import { z } from "zod";

// ============================================================================
// Date Validation Helpers
// ============================================================================

/**
 * Regex for YYYY-MM-DD date format.
 * Validates structure but not calendar correctness (e.g., 2024-02-30 would pass regex).
 * For full validation, use DateOnlySchema which uses z.string().date() for calendar correctness.
 */
export const DATE_ONLY_REGEX =
  /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/;

/**
 * Schema for DATEONLY fields returned by Sequelize as strings in YYYY-MM-DD format.
 * Uses Zod's built-in .date() validation which validates both format AND calendar correctness.
 * Nullable to support optional rule-specific dates (null = use agreement dates).
 */
export const DateOnlySchema = z.string().date().nullable();

// ============================================================================
// Nested Type Schemas
// ============================================================================

/**
 * Schema for product groups associated with a rebate rule.
 * Used for both qualifying and affected product groups.
 */
export const RebateRuleProductGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

/**
 * Schema for rebate tiers defining threshold-based rebate values.
 */
export const RebateRuleTierSchema = z.object({
  id: z.string().uuid(),
  tierOrder: z.number().int().min(1),
  // DECIMAL fields returned as strings by Sequelize
  thresholdValue: z.string(),
  rebateValue: z.string(),
});

/**
 * Schema for divisions associated with a rebate rule.
 */
export const RebateRuleDivisionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

// ============================================================================
// Main RebateRule Schema
// ============================================================================

/**
 * Zod schema that mirrors the RebateRule TypeScript interface from @erp/shared-types.
 * Used for runtime validation of rebate rule data from the database or API responses.
 */
export const RebateRuleSchema = z.object({
  id: z.string().uuid(),
  rebateAgreementId: z.string().uuid(),
  name: z.string(),
  // null for manual rules
  isRetroactiveToDollarOne: z.boolean().nullable(),
  isManual: z.boolean(),
  // null for manual rules
  thresholdType: z.string().nullable(),
  qualifyingInclusionType: z.string().nullable(),
  affectedInclusionType: z.string().nullable(),
  // DECIMAL field returned as string by Sequelize
  growthBaseline: z.string().nullable(),
  // DECIMAL field returned as string by Sequelize, "0" = no discount
  discountPercentage: z.string(),
  // Optional notes about the rebate rule
  notes: z.string().nullable(),
  // Order in which rules should be displayed and processed
  sortOrder: z.number().int(),
  // Rule-specific date range. If null, uses agreement dates. YYYY-MM-DD format.
  startDate: DateOnlySchema,
  endDate: DateOnlySchema,
  qualifyingProductGroups: z.array(RebateRuleProductGroupSchema),
  affectedProductGroups: z.array(RebateRuleProductGroupSchema),
  tiers: z.array(RebateRuleTierSchema),
  divisions: z.array(RebateRuleDivisionSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// ============================================================================
// Type Exports (derived from schemas)
// ============================================================================

export type RebateRuleProductGroupDto = z.infer<
  typeof RebateRuleProductGroupSchema
>;
export type RebateRuleTierDto = z.infer<typeof RebateRuleTierSchema>;
export type RebateRuleDivisionDto = z.infer<typeof RebateRuleDivisionSchema>;
export type RebateRuleDto = z.infer<typeof RebateRuleSchema>;
